import { useRef, type KeyboardEvent } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { DropdownMenu } from 'radix-ui';
import {
  Bot,
  Check,
  ChartGantt,
  ChevronDown,
  CircleUserRound,
  LogOut,
  PanelBottomClose,
  PanelBottomOpen,
  PanelRightClose,
  PanelRightOpen,
  Presentation,
  ShieldCheck,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { SessionUser } from '@/features/auth/api';
import { useLogout } from '@/features/auth/useSession';
import { MODE_LABEL, useUI, type Mode } from '@/state/store';
import { cn } from '@/lib/cn';
import { IconButton, Tip } from '@/components/ui/primitives';
import { OfflineBadge } from '@/features/offline';
import { DENSITIES, DENSITY_LABEL, THEMES, THEME_LABEL, isDensity, isTheme } from './appearance';
import { MODE_PANEL_ID, SHEET_BELOW, detailPresentation, modeTabId } from './layout';
import { useViewportWidth } from './useViewport';
import { NetworkPill } from '@/features/catalogue/NetworkPill';

const TOP_MODES = ['overview', 'maintenance', 'loader'] as const;
const MODE_ICON: Record<(typeof TOP_MODES)[number], LucideIcon> = {
  overview: Workflow,
  maintenance: Wrench,
  loader: ChartGantt,
};

/** Mode tabs carry their labels from xl (1280 px); below it they are icons with tooltips. */
const LABELLED_FROM = 1280;
/** From 2xl (1536 px) the presenter and admin buttons sit in the bar; below it they are in the user menu. */
const WIDE_FROM = 1536;

const menuItemClass =
  'flex cursor-pointer select-none items-center gap-2 rounded-control px-2.5 py-1.5 text-xs text-ink outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[highlighted]:bg-overlay';
const menuLabelClass = 'px-2.5 pb-1 pt-2 text-[10px] uppercase tracking-wider text-ink-3';

/**
 * The three primary modes as a tablist (role tab on the links, so a click or Enter
 * still navigates). Arrow keys, Home and End move the focus; Enter or Space
 * switches mode (manual activation: a switch mounts an engine, so focus alone
 * must not trigger it).
 */
function ModeTabs({ mode, labelled }: { mode: Mode; labelled: boolean }) {
  const tabs = useRef<Array<HTMLAnchorElement | null>>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = tabs.current.findIndex((t) => t === document.activeElement);
    if (current === -1) return;
    let next: number;
    if (e.key === 'ArrowRight') next = (current + 1) % TOP_MODES.length;
    else if (e.key === 'ArrowLeft') next = (current - 1 + TOP_MODES.length) % TOP_MODES.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TOP_MODES.length - 1;
    else if (e.key === ' ') {
      e.preventDefault();
      tabs.current[current]?.click();
      return;
    } else return;
    e.preventDefault();
    tabs.current[next]?.focus();
  };

  return (
    <nav className="flex shrink-0" aria-label="Modes">
      <div
        role="tablist"
        aria-label="Modes"
        aria-orientation="horizontal"
        className="flex items-center gap-0.5"
        onKeyDown={onKeyDown}
      >
        {TOP_MODES.map((m, i) => {
          const Icon = MODE_ICON[m];
          const selected = m === mode || (m === 'loader' && (mode === 'operations' || mode === 'analysis'));
          return (
            <Tip key={m} label={MODE_LABEL[m]} disabled={labelled}>
              <NavLink
                ref={(el) => {
                  tabs.current[i] = el;
                }}
                to={`/${m}`}
                id={modeTabId(m)}
                role="tab"
                aria-selected={selected}
                aria-controls={MODE_PANEL_ID}
                tabIndex={selected ? 0 : -1}
                className={cn(
                  'inline-flex h-8 items-center justify-center gap-1.5 rounded-control text-xs font-medium tracking-wide transition',
                  labelled ? 'px-2.5' : 'w-8',
                  selected ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-overlay hover:text-ink',
                )}
              >
                {!labelled && <Icon aria-hidden className="h-4 w-4" />}
                <span className={labelled ? undefined : 'sr-only'}>{MODE_LABEL[m]}</span>
              </NavLink>
            </Tip>
          );
        })}
      </div>
    </nav>
  );
}

function UserMenu({
  user,
  showName,
  menuPresenter,
  menuAdmin,
  menuGaia,
}: {
  user: SessionUser;
  showName: boolean;
  menuPresenter: boolean;
  menuAdmin: boolean;
  menuGaia: boolean;
}) {
  const density = useUI((s) => s.density);
  const theme = useUI((s) => s.theme);
  const setDensity = useUI((s) => s.setDensity);
  const setTheme = useUI((s) => s.setTheme);
  const togglePresenter = useUI((s) => s.togglePresenter);
  const gaiaOpen = useUI((s) => s.gaiaOpen);
  const setGaiaOpen = useUI((s) => s.setGaiaOpen);
  const navigate = useNavigate();
  const logout = useLogout();
  const name = user.name || user.email;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`User menu: ${name}`}
          data-testid="user-menu"
          className="flex h-9 min-w-0 shrink-0 items-center gap-2 rounded-control px-1.5 text-ink-2 transition hover:bg-overlay hover:text-ink data-[state=open]:bg-overlay"
        >
          {showName && (
            <span className="min-w-0 text-right leading-tight">
              <span className="block max-w-[140px] truncate text-xs text-ink">{name}</span>
              <span className="block text-[10px] uppercase tracking-wider text-ink-3">{user.role}</span>
            </span>
          )}
          <CircleUserRound aria-hidden className="h-5 w-5 shrink-0" />
          <ChevronDown aria-hidden className="h-3 w-3 shrink-0" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 min-w-[240px] rounded-panel border border-hairline-strong bg-raised p-1 shadow-elev-2"
        >
          <div className="px-2.5 py-2">
            <div className="truncate text-xs font-medium text-ink">{name}</div>
            {user.name && <div className="truncate text-[11px] text-ink-2">{user.email}</div>}
            <div className="text-[10px] uppercase tracking-wider text-ink-3">{user.role}</div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          <DropdownMenu.Label className={menuLabelClass}>Density</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={density} onValueChange={(v) => isDensity(v) && setDensity(v)}>
            {DENSITIES.map((d) => (
              <DropdownMenu.RadioItem
                key={d}
                value={d}
                className={menuItemClass}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="flex w-3.5 justify-center">
                  <DropdownMenu.ItemIndicator>
                    <Check aria-hidden className="h-3.5 w-3.5 text-accent" />
                  </DropdownMenu.ItemIndicator>
                </span>
                {DENSITY_LABEL[d]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          <DropdownMenu.Label className={menuLabelClass}>Theme</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={theme} onValueChange={(v) => isTheme(v) && setTheme(v)}>
            {THEMES.map((t) => (
              <DropdownMenu.RadioItem
                key={t}
                value={t}
                className={menuItemClass}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="flex w-3.5 justify-center">
                  <DropdownMenu.ItemIndicator>
                    <Check aria-hidden className="h-3.5 w-3.5 text-accent" />
                  </DropdownMenu.ItemIndicator>
                </span>
                {THEME_LABEL[t]}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          {menuGaia && (
            <DropdownMenu.Item className={menuItemClass} onSelect={() => setGaiaOpen(!gaiaOpen)}>
              <Bot aria-hidden className="h-3.5 w-3.5 text-ink-2" />
              {gaiaOpen ? 'Close GAIA' : 'GAIA assistant'}
            </DropdownMenu.Item>
          )}
          {menuPresenter && (
            <DropdownMenu.Item className={menuItemClass} onSelect={togglePresenter}>
              <Presentation aria-hidden className="h-3.5 w-3.5 text-ink-2" />
              Presenter mode (P)
            </DropdownMenu.Item>
          )}
          {menuAdmin && (
            <DropdownMenu.Item className={menuItemClass} onSelect={() => navigate('/admin')}>
              <ShieldCheck aria-hidden className="h-3.5 w-3.5 text-ink-2" />
              Admin console
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item className={menuItemClass} onSelect={() => void logout()}>
            <LogOut aria-hidden className="h-3.5 w-3.5 text-ink-2" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function TopBar({ mode, user }: { mode: Mode; user: SessionUser }) {
  const rightPanelOpen = useUI((s) => s.rightPanelOpen);
  const toggleRightPanel = useUI((s) => s.toggleRightPanel);
  const togglePresenter = useUI((s) => s.togglePresenter);
  const gaiaOpen = useUI((s) => s.gaiaOpen);
  const setGaiaOpen = useUI((s) => s.setGaiaOpen);
  const vw = useViewportWidth();
  const labelled = vw >= LABELLED_FROM;
  const wide = vw >= WIDE_FROM;
  const desk = vw >= SHEET_BELOW;
  const sheet = detailPresentation(vw) === 'sheet';
  const admin = user.role === 'admin' || user.role === 'superadmin';

  const PanelIcon = sheet
    ? rightPanelOpen
      ? PanelBottomClose
      : PanelBottomOpen
    : rightPanelOpen
      ? PanelRightClose
      : PanelRightOpen;

  return (
    <header
      className={cn(
        'flex h-12 shrink-0 items-center whitespace-nowrap border-b border-hairline bg-raised px-3',
        wide ? 'gap-3' : 'gap-2',
      )}
      data-testid="top-bar"
    >
      <div className="flex shrink-0 items-center gap-2 pr-1">
        <img src="/images/entarian-logo.svg" alt="Entarian" className={wide ? 'h-5' : 'h-4'} />
        {vw >= 768 && (
          <span className="font-display text-sm font-semibold tracking-[0.18em] text-ink">GEM DT</span>
        )}
      </div>

      <ModeTabs mode={mode} labelled={labelled} />

      <div className="flex min-w-0 flex-1 justify-center">
        {mode !== 'overview' && <NetworkPill maintenance={mode === 'maintenance'} />}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <OfflineBadge compact />
        {desk && (
          <IconButton
            label="GAIA assistant"
            active={gaiaOpen}
            aria-pressed={gaiaOpen}
            onClick={() => setGaiaOpen(!gaiaOpen)}
          >
            <Bot className="h-4 w-4" />
          </IconButton>
        )}
        {wide && admin && (
          <NavLink
            to="/admin"
            title="Admin console"
            aria-label="Admin console"
            className="inline-flex h-8 w-8 items-center justify-center rounded-control text-ink-2 transition hover:bg-overlay hover:text-ink"
          >
            <ShieldCheck className="h-4 w-4" />
          </NavLink>
        )}
        {wide && (
          <IconButton label="Presenter mode (P)" onClick={togglePresenter}>
            <Presentation className="h-4 w-4" />
          </IconButton>
        )}
        <IconButton
          label={rightPanelOpen ? 'Hide detail panel (])' : 'Show detail panel (])'}
          aria-expanded={rightPanelOpen}
          onClick={toggleRightPanel}
        >
          <PanelIcon className="h-4 w-4" />
        </IconButton>
        <div className="ml-1 flex items-center border-l border-hairline pl-2">
          <UserMenu
            user={user}
            showName={vw >= 1024}
            menuPresenter={!wide}
            menuAdmin={admin && !wide}
            menuGaia={!desk}
          />
        </div>
      </div>
    </header>
  );
}
